import { z } from 'zod';
import type {
  TargetTotalRow, SourceTableRow, StatusDescRow, StatusRow, SubstitutionRow,
  ApprovalCheckRow, PerTargetTotalsResponse, TargetTypeChangeRow, PlannedCallCountRow,
  ChangeBySourceRow, ExerciseRow, CustomColumnsRow, ReasonRow, DemotionReasonRow,
  UserRoleRow, UserTeamRow, AuditTrailRow, VacantSubstitutionRow, VacantTerritoryRow,
  HcpSearchRow, HcoSearchRow, ExUsSearchRow,
  HcpDetailsResponse, HcpPreviewResponse, HcpAlignmentResponse,
  HcoDetailsResponse, HcoAlignmentResponse,
  ExUsDetailsResponse, ExUsAlignmentResponse, HomeDetailsResponse,
  AddressLookupRow, AddressCheckResponse_v2, HcoRow, HcpRow,
} from './generated';

const ns = z.string().nullish();
const nn = z.number().nullish();
const nb = z.boolean().nullish();

export const TargetTotalRowSchema = z.object({
  TARGET_TYPE: ns, CURRENT_TOTAL: ns, FINAL_TOTAL: ns, START_CALL_TOTAL: ns,
  STATUS_ID: ns, TERR_ID: ns, TERR_NAME: ns, FIRST_NAME: ns, LAST_NAME: ns, STATUS: ns,
}) satisfies z.ZodType<TargetTotalRow>;

export const SourceTableRowSchema = z.object({
  PROF_ID: ns, FIRST_NAME: ns, LAST_NAME: ns, ADDRESS: ns, CITY: ns, STATE: ns,
  ZIP_CODE: ns, NPINUM: ns, MENUM: ns, MF_QUINTILE: ns, PV_QUINTILE: ns, MPN_QUINTILE: ns,
  TERR_ID: ns, TERR_NAME: ns, REGION_ID: ns, REGION_NAME: ns, SUGGESTED_TARGET_VALUE: ns,
  LEAD_CALL: ns, CURRENT_TARGET_FLAG: ns, CURRENT_TARGET_VALUE: ns, DEGREE: ns,
  CALLS_12MOS: ns, RX_12MOS: ns, PDRP_OPTOUT: ns, STATUS: ns, PRI_LOC: ns, BLDG_ID: ns,
  AM_RATING: ns, PV_HT: ns, SPEC1: ns, SPEC1_DESC: ns, SPEC2: ns, SPEC2_DESC: ns,
  CUSTOM_COLUMNS: ns, TARGET_TYPE: ns, REASON: ns, NOTES: ns,
  PEM_TARGET: ns, PEM_TARGET_TYPE: ns, TGT: ns,
}) satisfies z.ZodType<SourceTableRow>;

export const StatusDescRowSchema = z.object({
  STATUS_ID: ns, TEAM_ID: ns, STATUS: ns, DESCRIPTION: ns,
}) satisfies z.ZodType<StatusDescRow>;

export const StatusRowSchema = z.object({
  statusID: ns, team: ns, STATUS: ns, DESCRIPTION: ns,
}) satisfies z.ZodType<StatusRow>;

export const SubstitutionRowSchema = z.object({
  TARGETING_ID: ns, ID: ns, VACANT_TERR_ID: ns, VACANT_TERR_NAME: ns, SUB_TERR_ID: ns, UserEmail: ns,
}) satisfies z.ZodType<SubstitutionRow>;

export const ApprovalCheckRowSchema = z.object({
  TARGETING_ID: ns,
}) satisfies z.ZodType<ApprovalCheckRow>;

export const TargetTypeChangeRowSchema = z.object({
  NEW_TARGET_VALUE: ns, COUNT_OF_NEW_TARGET_VALUE: ns,
}) satisfies z.ZodType<TargetTypeChangeRow>;

export const PlannedCallCountRowSchema = z.object({
  TARGET_VALUE: ns, COUNT_OF_PLANNED_CALLS: ns,
}) satisfies z.ZodType<PlannedCallCountRow>;

export const ChangeBySourceRowSchema = z.object({
  SOURCE_TYPE: ns, COUNT_OF_CHANGES: ns,
}) satisfies z.ZodType<ChangeBySourceRow>;

export const PerTargetTotalsResponseSchema = z.object({
  targetTypesWithChanges: TargetTypeChangeRowSchema.array().nullish(),
  plannedCallCounts: PlannedCallCountRowSchema.array().nullish(),
  changesBySourceType: ChangeBySourceRowSchema.array().nullish(),
  totalChangesInTerritory: ns,
  totalProfilesInTerritory: ns,
}) satisfies z.ZodType<PerTargetTotalsResponse>;

export const ExerciseRowSchema = z.object({
  ID: ns, TARGETING_EXERCISE_ID: ns, NAME: ns, TEAM: ns, CUSTOMER_TYPE: ns,
  COLUMNS: ns, START_DATE: ns, END_DATE: ns, MAX_CHANGES_PER_TERRITORY: ns,
  EXERCISE_TYPE: ns, PER_TARGET_LIMITS: ns, MIN_CALLS_PER_TERRITORY: ns,
  MAX_CALLS_PER_TERRITORY: ns, MIN_CHANGES_PER_TERRITORY: ns,
  MAX_UPGRADES_PER_TERRITORY: ns, MAX_DOWNGRADES_PER_TERRITORY: ns, LIMIT_TYPE: ns,
}) satisfies z.ZodType<ExerciseRow>;

export const CustomColumnsRowSchema = z.object({
  TARGETING_EXERCISE_ID: ns, CUSTOM_COLUMNS: ns,
}) satisfies z.ZodType<CustomColumnsRow>;

export const ReasonRowSchema = z.object({
  ID: ns, TARGETING_ID: ns, REASON: ns,
}) satisfies z.ZodType<ReasonRow>;

export const DemotionReasonRowSchema = z.object({
  ID: ns, TARGETING_ID: ns, IS_UPGRADE: nb, REASON: ns,
}) satisfies z.ZodType<DemotionReasonRow>;

export const UserRoleRowSchema = z.object({
  Id: ns, Name: ns,
}) satisfies z.ZodType<UserRoleRow>;

export const UserTeamRowSchema = z.object({
  email: ns, name: ns, TEAM_ID: ns, TEAM_NAME: ns,
}) satisfies z.ZodType<UserTeamRow>;

export const AuditTrailRowSchema = z.object({
  ID: z.number(), USER_NAME: ns, EVENT_NAME: ns, EVENT_DT: z.string(), EVENT_DESC: ns, FULL_REQUEST: ns,
}) satisfies z.ZodType<AuditTrailRow>;

export const VacantSubstitutionRowSchema = z.object({
  SUB_TERR_ID: ns, SUB_EMAIL: ns, VACANT_TERR_ID: ns, VACANT_TERR_NAME: ns,
  FIRST_NAME: ns, LAST_NAME: ns,
}) satisfies z.ZodType<VacantSubstitutionRow>;

export const VacantTerritoryRowSchema = z.object({
  VACANT_TERR_ID: ns, VACANT_TERR_NAME: ns, SUB_TERR_ID: ns,
}) satisfies z.ZodType<VacantTerritoryRow>;

export const HcpSearchRowSchema = z.object({
  PROF_ID: nn, PROF_ID_NEW: nn, STATUS: ns, FIRST_NAME: ns, MIDDLE_NAME: ns, LAST_NAME: ns,
  DEGREE: ns, SPECIALTY: ns, TAXONOMY_CODE: ns, NPINUM: ns,
}) satisfies z.ZodType<HcpSearchRow>;

export const HcoSearchRowSchema = z.object({
  ORG_ID: nn, ORG_ID_NEW: nn, PARENT_ORG_ID: nn, LOC_ID: nn, BLDG_ID: nn,
  ORG_NAME: ns, CTMS_NAME: ns, ORG_STATUS: ns, ORG_TYPE: ns,
  ADDRESS1: ns, ADDRESS2: ns, CITY: ns, STATE: ns,
  ZIP5: ns, ADDR_VALID: ns, ADDR_TYPE: ns, PHONE: ns, FAX: ns,
  PREFERRED_NAME: ns, NABP_ID: ns, OUTLET_ID: ns, EMAIL: ns, WEBSITE: ns,
  MAIN_CONTACT: ns, NO_CONTACT: ns, NOTES: ns, AGGSPEND_FLAG: ns, AGGSPEND_STATUS: ns,
  TERR_ID: ns, MDM_OVERRIDE: ns, MDM_TERR_ID: ns,
  AFFILIATION_COUNT: nn, IN_NETWORK_PHARMACY: ns,
}) satisfies z.ZodType<HcoSearchRow>;

export const ExUsSearchRowSchema = z.object({
  FIRST_NAME: ns, LAST_NAME: ns, DEGREE: ns, SPECIALTY: ns, CITY: ns, STATE: ns,
  POSTAL_CODE: ns, COUNTRY_CODE: ns, DOB: ns, PHONE: ns, EMAIL: ns,
}) satisfies z.ZodType<ExUsSearchRow>;

const passRow = z.object({}).passthrough();

export const HcpDetailsResponseSchema = z.object({
  data: passRow.array().nullish(), affiliations: passRow.array().nullish(),
  changeRequests: passRow.array().nullish(), regionalPlan: passRow.array().nullish(),
  addresses: passRow.array().nullish(), secondaryAddresses: passRow.array().nullish(),
  primaryAddress: passRow.array().nullish(), bestAddress: passRow.array().nullish(),
  addressHistory: passRow.array().nullish(), sln: passRow.array().nullish(),
  ctmsAddress: passRow.array().nullish(),
}) satisfies z.ZodType<HcpDetailsResponse>;

export const HcpPreviewResponseSchema = z.object({
  data: passRow.array().nullish(),
  ctmsAddress: passRow.array().nullish(),
}) satisfies z.ZodType<HcpPreviewResponse>;

export const HcpAlignmentResponseSchema = z.object({
  data: passRow.array().nullish(),
}) satisfies z.ZodType<HcpAlignmentResponse>;

export const HcoDetailsResponseSchema = z.object({
  data: passRow.array().nullish(), affiliations: passRow.array().nullish(),
  changeRequests: passRow.array().nullish(), tdddHistory: passRow.array().nullish(),
}) satisfies z.ZodType<HcoDetailsResponse>;

export const HcoAlignmentResponseSchema = z.object({
  data: passRow.array().nullish(),
}) satisfies z.ZodType<HcoAlignmentResponse>;

export const ExUsDetailsResponseSchema = z.object({
  data: passRow.array().nullish(), changeRequests: passRow.array().nullish(),
  addresses: passRow.array().nullish(), secondaryAddresses: passRow.array().nullish(),
  primaryAddress: passRow.array().nullish(), addressHistory: passRow.array().nullish(),
}) satisfies z.ZodType<ExUsDetailsResponse>;

export const ExUsAlignmentResponseSchema = z.object({
  data: passRow.array().nullish(),
}) satisfies z.ZodType<ExUsAlignmentResponse>;

export const HomeDetailsResponseSchema = z.object({
  activity: passRow.array().nullish(),
  submitted: passRow.array().nullish(),
}) satisfies z.ZodType<HomeDetailsResponse>;

export const HcoRowSchema = z.object({
  ORG_ID: nn, ORG_NAME: ns,
}) satisfies z.ZodType<HcoRow>;

export const HcpRowSchema = z.object({
  PROF_ID: nn, FIRST_NAME: ns, LAST_NAME: ns,
}) satisfies z.ZodType<HcpRow>;

export const AddressLookupRowSchema = z.object({
  ADDRESS1: ns, ADDRESS2: ns, CITY: ns, STATE: ns, ZIP5: ns, ADDR_VALID: ns,
}) satisfies z.ZodType<AddressLookupRow>;

export const AddressCheckResponseSchema = z.object({
  status: z.boolean().optional(),
}) satisfies z.ZodType<AddressCheckResponse_v2>;
